import * as THREE from 'three';
import type { Vec3 } from '../../sim/mathx';

const DEG2RAD = Math.PI / 180;
const AZIMUTH_SEGMENTS = 32;

export interface CoverageVolumeConfig {
  mountEnu: Vec3;
  mountYaw: number;
  azSector: { centerDeg: number; widthDeg: number };
  elLimits: { minDeg: number; maxDeg: number };
  rangeMin: number;
  rangeMax: number;
}

/** Convert radar-relative azimuth and horizontal range to an ENU point. */
export const azimuthRangeToEnu = (
  mountEnu: Vec3,
  mountYaw: number,
  azimuthRad: number,
  rangeM: number,
): Vec3 => {
  const worldAzimuth = mountYaw + azimuthRad;
  return {
    x: mountEnu.x + Math.cos(worldAzimuth) * rangeM,
    y: mountEnu.y + Math.sin(worldAzimuth) * rangeM,
    z: mountEnu.z,
  };
};

const wrapPi = (angle: number): number => {
  const wrapped = (((angle + Math.PI) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  return wrapped - Math.PI;
};

/** Persistent, procedural radar coverage wedge. */
export class CoverageVolume extends THREE.Group {
  readonly mountEnu: Vec3;
  readonly mountYaw: number;
  readonly azSector: { centerDeg: number; widthDeg: number };
  readonly elLimits: { minDeg: number; maxDeg: number };
  readonly mesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;

  private minRange: number;
  private maxRange: number;
  private disposed = false;

  constructor(config: CoverageVolumeConfig) {
    super();
    this.name = 'radar-viz:coverage';
    this.mountEnu = { ...config.mountEnu };
    this.mountYaw = config.mountYaw;
    this.azSector = { ...config.azSector };
    this.elLimits = { ...config.elLimits };
    this.minRange = config.rangeMin;
    this.maxRange = config.rangeMax;
    this.validateRange(this.minRange, this.maxRange);

    const geometry = this.createGeometry();
    const material = new THREE.MeshBasicMaterial({
      color: 0x3aa7c9,
      transparent: true,
      opacity: 0.1,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.name = 'radar-viz:coverage-mesh';
    this.mesh.frustumCulled = false;
    this.add(this.mesh);
    this.position.set(this.mountEnu.x, this.mountEnu.y, this.mountEnu.z);
    this.writePositions();
  }

  get rangeMin(): number {
    return this.minRange;
  }

  get rangeMax(): number {
    return this.maxRange;
  }

  farEdgePoint(azimuthRad: number): Vec3 {
    return azimuthRangeToEnu(this.mountEnu, this.mountYaw, azimuthRad, this.maxRange);
  }

  containsAzimuth(azimuthRad: number): boolean {
    const center = this.azSector.centerDeg * DEG2RAD;
    const halfWidth = this.azSector.widthDeg * DEG2RAD * 0.5;
    return Math.abs(wrapPi(azimuthRad - center)) <= halfWidth + Number.EPSILON;
  }

  /** Resize by rewriting the existing dynamic position buffer. */
  setRange(rangeMin: number, rangeMax: number): void {
    this.validateRange(rangeMin, rangeMax);
    this.minRange = rangeMin;
    this.maxRange = rangeMax;
    this.writePositions();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.removeFromParent();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
    this.clear();
  }

  private createGeometry(): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array((AZIMUTH_SEGMENTS + 1) * 4 * 3);
    const position = new THREE.BufferAttribute(positions, 3);
    position.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('position', position);

    const indices: number[] = [];
    const addQuad = (a: number, b: number, c: number, d: number): void => {
      indices.push(a, b, c, a, c, d);
    };
    for (let segment = 0; segment < AZIMUTH_SEGMENTS; segment += 1) {
      const a = segment * 4;
      const b = (segment + 1) * 4;
      addQuad(a, b, b + 2, a + 2);
      addQuad(a + 1, a + 3, b + 3, b + 1);
      addQuad(a, a + 1, b + 1, b);
      addQuad(a + 2, b + 2, b + 3, a + 3);
    }
    addQuad(0, 2, 3, 1);
    const end = AZIMUTH_SEGMENTS * 4;
    addQuad(end, end + 1, end + 3, end + 2);
    geometry.setIndex(indices);
    geometry.computeBoundingSphere();
    return geometry;
  }

  private writePositions(): void {
    const attribute = this.mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
    const output = attribute.array as Float32Array;
    const center = this.azSector.centerDeg * DEG2RAD;
    const width = this.azSector.widthDeg * DEG2RAD;
    const minElevation = this.elLimits.minDeg * DEG2RAD;
    const maxElevation = this.elLimits.maxDeg * DEG2RAD;
    for (let segment = 0; segment <= AZIMUTH_SEGMENTS; segment += 1) {
      const azimuth = center - width / 2 + (width * segment) / AZIMUTH_SEGMENTS;
      const ranges = [this.minRange, this.minRange, this.maxRange, this.maxRange];
      const elevations = [minElevation, maxElevation, minElevation, maxElevation];
      for (let corner = 0; corner < 4; corner += 1) {
        const slantRange = ranges[corner]!;
        const elevation = elevations[corner]!;
        const horizontalRange = slantRange * Math.cos(elevation);
        const point = azimuthRangeToEnu(this.mountEnu, this.mountYaw, azimuth, horizontalRange);
        const offset = (segment * 4 + corner) * 3;
        output[offset] = point.x - this.mountEnu.x;
        output[offset + 1] = point.y - this.mountEnu.y;
        output[offset + 2] = slantRange * Math.sin(elevation);
      }
    }
    attribute.needsUpdate = true;
    this.mesh.geometry.computeBoundingBox();
    this.mesh.geometry.computeBoundingSphere();
  }

  private validateRange(rangeMin: number, rangeMax: number): void {
    if (!Number.isFinite(rangeMin) || rangeMin < 0) {
      throw new RangeError('CoverageVolume rangeMin must be finite and >= 0');
    }
    if (!Number.isFinite(rangeMax) || rangeMax <= rangeMin) {
      throw new RangeError('CoverageVolume rangeMax must be finite and greater than rangeMin');
    }
  }
}
